/**
 * resolveUploadContentType — pick a Storage contentType that the
 * `storage.rules` allowlists accept (image/* without SVG, or application/pdf).
 *
 * Browsers sometimes hand us a File with a blank or unhelpful `.type` — PDFs
 * and HEIC are common offenders. `uploadBytes` then stores the object as
 * application/octet-stream, which the rules reject with an opaque
 * `storage/unauthorized`. The measurement-file and payment-receipt upload
 * paths both route through this so the stored object is correctly typed —
 * pass the result as `uploadBytes(ref, file, { contentType })`.
 */

const CONTENT_TYPE_BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif',
  gif: 'image/gif',
};

const ACCEPTED_UPLOAD_CONTENT_TYPES = new Set(Object.values(CONTENT_TYPE_BY_EXTENSION));

/**
 * An accepted contentType for `file`, or `null` when it is neither an allowed
 * image nor a PDF — callers should surface a clear error rather than let the
 * upload fail opaquely against the storage rules.
 */
export const resolveUploadContentType = (file: File): string | null => {
  const declared = (file.type || '').trim().toLowerCase();
  if (ACCEPTED_UPLOAD_CONTENT_TYPES.has(declared)) return declared;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return CONTENT_TYPE_BY_EXTENSION[ext] ?? null;
};
